/**
 * Collects and confirms a Greenhouse hostname prefix.
 *
 * 1. Parse the current input at the form boundary.
 * 2. Show the Greenhouse test link only for a valid prefix.
 * 3. Let the caller own the mutation and dialog lifecycle.
 *
 * The next file in the connect flow is GreenhouseOauthSubdomainDialog, which supplies the shared
 * dialog shell and the OAuth or update action.
 */

import React, { FC, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Button, Checkbox, FormControlLabel, Link, Stack, TextField, Typography } from '@mui/material';

import { GreenhouseSubdomain, parseGreenhouseSubdomain } from '../GreenhouseSubdomain';

/** Greenhouse sign-in page used for instructions and the invalid-input test link. */
const GREENHOUSE_SIGN_IN_URL = 'https://app.greenhouse.io/users/sign_in';

/** Props for the reusable Greenhouse subdomain form. */
interface Props {
  /** Subdomain shown when the form opens. */
  initialSubdomain?: string;
  /** Disables input and Save while the caller submits. */
  isSubmitting?: boolean;
  /** Closes the containing dialog without saving. */
  onCancel: () => void;
  /** Sends the parsed subdomain to the caller's existing save flow. */
  onSubmit: (subdomain: GreenhouseSubdomain) => void;
}

/** Renders Greenhouse subdomain instructions, validation, confirmation, and actions. */
const GreenhouseSubdomainPanel: FC<Props> = ({ initialSubdomain = '', isSubmitting = false, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const [subdomain, setSubdomain] = useState(initialSubdomain);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const parsedSubdomain = parseGreenhouseSubdomain(subdomain);
  const isValid = parsedSubdomain.isOk();
  const subdomainConfirmationLink = isValid ? `https://${parsedSubdomain.value}.greenhouse.io` : GREENHOUSE_SIGN_IN_URL;

  return (
    <Stack spacing={2} direction="column">
      <Stack spacing={0.5} direction="column">
        <Typography variant="subtitle1">
          {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.subdomainTitle')}
        </Typography>
        <Typography color="text.secondary">
          {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.subdomainDescription')}{' '}
          <Link underline="none" href={GREENHOUSE_SIGN_IN_URL} rel="noopener noreferrer" target="_blank">
            {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.loginToGreenhouse')}
          </Link>
        </Typography>
      </Stack>
      <img
        alt={t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.subdomainAlt')}
        src="/static/images/integrations/greenhouse-subdomain.png"
      />
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextField
            sx={{ width: '120px' }}
            disabled={isSubmitting}
            error={subdomain.length > 0 && !isValid}
            value={subdomain}
            placeholder={t(
              'hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.subdomainPlaceholder'
            )}
            inputProps={{
              'aria-label': t(
                'hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.subdomainTitle'
              ),
            }}
            onChange={(event) => {
              setSubdomain(event.target.value);
              setIsConfirmed(false);
            }}
          />
          <Typography>
            {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.greenhouseDomainSuffix')}
          </Typography>
        </Stack>
        {isValid ? (
          <Link underline="none" href={subdomainConfirmationLink} rel="noopener noreferrer" target="_blank">
            {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.testSubdomainLink')}
          </Link>
        ) : (
          <Typography color="text.secondary">
            {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.testSubdomainLink')}
          </Typography>
        )}
      </Stack>
      <FormControlLabel
        label={
          <Typography color={isValid ? '' : 'text.secondary'}>
            {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseDialog.subdomainConfirmation')}
          </Typography>
        }
        control={
          <Checkbox
            disabled={isSubmitting || !isValid}
            checked={isConfirmed}
            onChange={() => setIsConfirmed((value) => !value)}
          />
        }
      />
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button color="inherit" onClick={onCancel} disabled={isSubmitting}>
          {t('hiring-dashboard-common:common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => parsedSubdomain.map(onSubmit)}
          disabled={isSubmitting || !isConfirmed || !isValid}
        >
          {t('hiring-dashboard-common:common.save')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default GreenhouseSubdomainPanel;
